/**
 * The way back from the builder of a running plan to its other two views, Przegląd and Kalendarz.
 *
 * Owner, 30 September 2026: the plan screen carries a Przegląd · Kalendarz · Programowanie switch,
 * and the builder it opens has to carry the same one, so the trainer moves between the three without
 * a detour through the client. Those two views live at `/klienci/$clientId/plan/$assignmentId`, which
 * the builder's own address does not name — so the assignment is read from the trainer's assignment
 * list, the same read (and the same cached query) the plan menu's endings already make.
 *
 * `null` on a library plan, on a plan whose read has not answered, and on a fork the list does not
 * name a client for: a switch whose two other views lead nowhere is not drawn at all.
 */

import { useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';

import { planTemplateAssignmentsQueryOptions, useIsForkPlan } from '~/entities/plan-draft';

export interface PlanViewLinks {
  readonly onOpenOverview: () => void;
  readonly onOpenCalendar: () => void;
}

export function usePlanViewLinks(planId: number | null): PlanViewLinks | null {
  const navigate = useNavigate();
  const isFork = useIsForkPlan();
  const assignments = useQuery({
    ...planTemplateAssignmentsQueryOptions(planId ?? 0),
    enabled: planId !== null && isFork,
  });

  const fork = assignments.data?.fork ?? null;
  if (!isFork || fork === null || fork.customerId === null) return null;

  const params = { clientId: String(fork.customerId), assignmentId: String(fork.assignmentId) };
  return {
    onOpenOverview: () => {
      void navigate({ to: '/klienci/$clientId/plan/$assignmentId', params, search: {} });
    },
    onOpenCalendar: () => {
      void navigate({
        to: '/klienci/$clientId/plan/$assignmentId',
        params,
        search: { view: 'calendar' },
      });
    },
  };
}
